import { useState, type ComponentProps } from 'react';
import { Modal } from './forms';
import { Icon } from './Icon';
import { ApiError } from '../lib/api';
import {
  useEphemeralToast,
  useUpdateTenantSettings,
} from '../lib/queries';

// Icon.name es una union literal interna del componente Icon. Reusamos el
// tipo del prop directamente para no acoplar a un export type que no existe.
type IconName = ComponentProps<typeof Icon>['name'];

/**
 * PRICE-C — Modal de onboarding que aparece la primera vez que el comerciante
 * entra al panel sin haber seteado `tenant.settings.pricingMode` explícitamente.
 *
 * Decide entre:
 * - 'single': una sola moneda (recomendado, simplifica la UI).
 * - 'multi': varias monedas (PriceLists separadas por canal/mercado).
 *
 * El comerciante puede cambiar la decisión después en /configuracion, sub-sección
 * "Modo de precios" — pero esta es la primera elección informada que define el
 * tono de la experiencia. NO se cierra por backdrop ni Escape: es decisión
 * importante y no debe perderse por click accidental.
 */
interface PricingModeOnboardingProps {
  open: boolean;
  onClose: () => void;
}

interface OptionConfig {
  icon: IconName;
  title: string;
  description: string;
  recommended?: boolean;
}

const OPTIONS: Record<'single' | 'multi', OptionConfig> = {
  single: {
    icon: 'package',
    title: 'Una sola moneda',
    description:
      'Tus productos tienen un solo precio. Es lo más común — perfecto si vendes en un solo país.',
    recommended: true,
  },
  multi: {
    icon: 'cart',
    title: 'Varias monedas',
    description:
      'Algunos productos se venden en distintas monedas (ej. COP nacional + USD internacional). Te ayudamos a configurar listas de precios separadas por canal.',
  },
};

export function PricingModeOnboarding({
  open,
  onClose,
}: PricingModeOnboardingProps) {
  const mutation = useUpdateTenantSettings();
  const [toast, setToast] = useEphemeralToast();
  const [selecting, setSelecting] = useState<'single' | 'multi' | null>(null);

  const handleChoose = async (mode: 'single' | 'multi') => {
    if (mutation.isPending) return;
    setSelecting(mode);
    try {
      await mutation.mutateAsync({ pricingMode: mode });
      // Cerrar después del éxito; el query invalida y la prop `open` del
      // caller pasa a false en el próximo render.
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      setToast({ kind: 'error', msg });
      setSelecting(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="¿En cuántas monedas vendes?"
      description="Esto define cómo se muestran los precios en tu panel. Puedes cambiarlo después en Configuración."
      size="md"
      dismissOnBackdrop={false}
      busy={mutation.isPending}
      ariaLabel="Elegir modo de precios"
    >
      <div className="pricing-mode-options">
        {(['single', 'multi'] as const).map((key) => {
          const opt = OPTIONS[key];
          const isLoading = selecting === key && mutation.isPending;
          return (
            <button
              key={key}
              type="button"
              className="pricing-mode-option"
              onClick={() => handleChoose(key)}
              disabled={mutation.isPending}
              aria-label={opt.title}
            >
              <span className="pricing-mode-option-icon" aria-hidden="true">
                <Icon name={opt.icon} size={20} />
              </span>
              <span className="pricing-mode-option-body">
                <span className="pricing-mode-option-title">
                  {opt.title}
                  {opt.recommended ? (
                    <span className="pricing-mode-option-badge">
                      Recomendado
                    </span>
                  ) : null}
                </span>
                <span className="pricing-mode-option-desc">
                  {opt.description}
                </span>
              </span>
              {isLoading ? (
                <span className="pricing-mode-option-loading">Guardando…</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {toast ? (
        <p
          className={toast.kind === 'ok' ? 'toast-ok' : 'toast-error'}
          style={{ marginTop: 12, fontSize: 12 }}
          role="status"
        >
          {toast.msg}
        </p>
      ) : null}

      <style>{`
        .pricing-mode-options {
          display: grid;
          gap: 12px;
        }
        .pricing-mode-option {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 16px;
          background: var(--bg-1);
          border: 1px solid var(--border-1);
          border-radius: 8px;
          cursor: pointer;
          text-align: left;
          color: var(--text-1);
          font: inherit;
          transition: border-color 120ms ease, background 120ms ease;
        }
        .pricing-mode-option:hover:not(:disabled) {
          border-color: var(--accent, #22c55e);
          background: var(--bg-2);
        }
        .pricing-mode-option:focus-visible {
          outline: 2px solid var(--accent, #22c55e);
          outline-offset: 2px;
        }
        .pricing-mode-option:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .pricing-mode-option-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: var(--bg-2);
          color: var(--text-2);
          flex-shrink: 0;
        }
        .pricing-mode-option-body {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }
        .pricing-mode-option-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-1);
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .pricing-mode-option-badge {
          font-size: 10px;
          font-weight: 500;
          text-transform: uppercase;
          padding: 2px 6px;
          border-radius: 4px;
          background: var(--accent, #22c55e);
          color: #fff;
          letter-spacing: 0.04em;
        }
        .pricing-mode-option-desc {
          font-size: 12px;
          color: var(--text-3);
          line-height: 1.45;
        }
        .pricing-mode-option-loading {
          font-size: 11px;
          color: var(--text-3);
          align-self: center;
        }
      `}</style>
    </Modal>
  );
}

export default PricingModeOnboarding;
